import create, { UseStore } from 'zustand';
import { documentResolver } from './document-resolver';
import { completeState, Diagnosis, matchAction, Procedure, Visit, Worksheet, WorksheetSummary } from './domain';

import { WorksheetStateRef } from './transition-api';
import { WorksheetApis } from './visit-context';
import { WorksheetResources } from './worksheet-selection-api';
import { modifierLinkString } from './procedure';
import { refreshSummary } from './document-viewer/document-api';

export const codingTabIndex = create(() => ({
  index: 0,
}));

type WorksheetTuple = [WorksheetApis?, Worksheet?, WorksheetSummary?, boolean?];

type HospitalistData = {
  tuples: WorksheetTuple[];
  selectedId: string;
  changed: boolean;
  lastWorksheet: boolean;
};

export type HospitalistContext = {
  data: UseStore<HospitalistData>;
  apis: WorksheetApis[];
  selectedApis: () => WorksheetApis;
  getWorksheet: (i?: number) => WorksheetApis;
  setWorksheet: (id: string) => void;
  apisById: (id: string) => WorksheetApis;
  updateSummary: (a: WorksheetStateRef, id?: string) => void;
  buildTuples: (id?: string, sort?: boolean) => WorksheetTuple[];
  addCpt: (a: Procedure, id?: string) => void;
  addIcd: (a: Diagnosis, id?: string) => void;
  allIcd: (a: Diagnosis[], id?: string) => void;
  allCpt: (a: Procedure[], id?: string) => void;
  copyWorksheet: (a: Worksheet, id?: string) => void;
  updateDocuments: (id: string, summary?: WorksheetSummary) => void;
  quit: (a: WorksheetStateRef) => Promise<any>;
  destroy: () => void;
  closeTransitionDialogs: () => void;
};

type HospitalistContextProps = {
  visit: Visit;
  worksheets: WorksheetApis[];
  resources: WorksheetResources;
  getApiById: (id: string) => WorksheetApis;
  getApis: () => WorksheetApis[];
  onComplete: () => void;
  onWorksheetChanged?: (id: string) => void;
};

export function hospitalistContext({ visit, worksheets, resources, onComplete, getApiById, getApis, onWorksheetChanged }: HospitalistContextProps): HospitalistContext {
  let api: WorksheetApis;

  function setWorksheetIndex(index: number = 0) {
    index = Math.max(0, index || 0);
    index = index % worksheets.length;
    api = worksheets[index];
  }
  setWorksheetIndex(0);

  function getWorksheet(i?: number) {
    if (isNaN(i) && api) {
      return api;
    } else {
      setWorksheetIndex(i);
    }
    return api;
  }

  const data = create(() => ({
    tuples: [],
    selectedId: null,
    changed: false,
    lastWorksheet: false,
  }));

  let apiSubscriptions: Record<string, () => void>;
  let selectSubscriptions = [];
  const tabSubscription = codingTabIndex.subscribe((s) => {
    setWorksheetIndex(s.index);
    const w = worksheets[s.index];
    if (w) {
      const id = w.context.worksheet.worksheetId;
      const selection = w.context.selection;
      const summary = selection.summaryById(id);
      updateDocuments(id, summary);
    }
  });

  clearApiSubscriptions();
  function addApiSubscription(id: string) {
    function update(sorted = true, newId?: string) {
      buildTuples(newId || id, sorted);
    }

    if (id) {
      const api = apisById(id);
      if (api && !apiSubscriptions[id]) {
        const { icds, cpts } = api.icdCpt;

        icds.events.values(() => doSort && update(false));
        cpts.events.values(() => doSort && update(false));

        const { data } = api.context.selection;
        selectSubscriptions.push(
          data.subscribe(
            () => update(false),
            (s) => s.sorted
          )
        );
        selectSubscriptions.push(
          data.subscribe(
            () => update(true),
            (s) => s.selected
          )
        );

        apiSubscriptions[id] = () => {
          icds.events.unsubscribe();
          cpts.events.unsubscribe();
          selectSubscriptions.forEach((f) => f());
        };
      }
    }
  }

  function getCurrentWorksheetData(selectedApi: WorksheetApis, sorted?: Worksheet[]) {
    const selection = selectedApi.context.selection;
    const tuples: WorksheetTuple[] = [];
    const worksheets = sorted || selection.data.getState().sorted || [];

    worksheets.forEach((u) => {
      const summary = selection.summaryById(u.worksheetId);
      if (summary.editable) {
        const apis = apisById(u.worksheetId);
        if (apis) {
          const sheet = apis.icdCpt.current();
          apis.context.worksheet = sheet;

          tuples.push([apis, sheet, summary]);
        } else {
          tuples.push([selectedApi, u, summary]);
        }
      } else {
        tuples.push([selectedApi, u, summary]);
      }
    });
    return tuples;
  }

  function buildTuples(id?: string, sort = false): WorksheetTuple[] {
    const selectedApi = selectedApis(id);
    const selection = selectedApi.context.selection;

    const selectedId = selection.data.getState().selected?.worksheet?.worksheetId || selectedApi.context.worksheet.worksheetId;

    addApiSubscription(selectedId);
    let sorted: Worksheet[];
    if (sort) {
      sorted = selection.sortWorksheets(['date'], false, selectedId);
    } else {
      sorted = selection.data.getState().sorted || [];
    }
    const tuples = getCurrentWorksheetData(selectedApi, sorted);
    data.setState({ tuples, selectedId });

    return tuples;
  }

  function setWorksheet(id: string) {
    let index = worksheets.indexOf(worksheets.find((a) => a.context.worksheet.worksheetId === id));
    setWorksheetIndex(index);
    if (onWorksheetChanged) {
      onWorksheetChanged(id);
    }
  }

  function clearApiSubscriptions() {
    Object.values((a: () => void) => a());
    apiSubscriptions = {};
  }
  function destroy() {
    codingTabIndex.setState({ index: 0 });
    tabSubscription();
    clearApiSubscriptions();
  }

  function complete() {
    let result = true;

    worksheets.forEach((a) => {
      const readonly = a.context.selection.complete();
      result = result && readonly;

      if (worksheets.length > 1) {
        a.context.readonly = readonly;
        a.context.calculator.setState({ readonly });
      }
    });
    data.setState({ lastWorksheet: result });
    return result;
  }

  const { previousSummaries, components } = resources;

  async function transitionToPrevious(id: string): Promise<any> {
    const requests: Promise<any>[] = [];
    visitOtherTabs(id, async (a) => {
      const k = a.context.worksheet.worksheetId;
      const c = previousSummaries[k];
      const d = components[k];
      if (c?.stateInfo && d?.stateInfo) {
        const action = matchAction(c.stateInfo.name, d.stateInfo?.actions);
        if (action) {
          console.log('previous state', c.stateInfo, d.stateInfo);
          requests.push(a.transition.getState().transition(action));
        }
      }
    });
    return Promise.all(requests);
  }
  function quit(a: WorksheetStateRef): Promise<any> {
    const c = getWorksheet();
    if (c) {
      c.context.selection.updateSummary(a);
      destroy();
      return transitionToPrevious(c.context.worksheet.worksheetId);
    } else {
      destroy();
      return Promise.resolve();
    }
  }

  function updateDocuments(pendingId: string, summary?: WorksheetSummary) {
    buildTuples(pendingId, true);
    visit.worksheetId = pendingId;
    documentResolver.openWindow(visit);
    if (summary) {
      refreshSummary.postMessage(summary);
    }
  }

  let selectedId: string;
  function updateSummary(a: WorksheetStateRef) {
    if (a) {
      selectedId = a.worksheetId;
      buildTuples(selectedId);

      const finshed = completeState(a.stateInfo.name);
      const done = complete();
      const selection = apisById(selectedId).context.selection;
      const summary = selection.summaryById(selectedId);
      if (finshed) {
        if (done) {
          onComplete();
        } else {
          const summary = selection.summaryById(selectedId);
          const nextInTab = selection.nextWorksheet(selectedId);
          if (nextInTab) {
            const worksheetId = nextInTab.worksheet.worksheetId;

            selection.setSelected(worksheetId);
            selection.updateSummary({ stateInfo: nextInTab.summary.stateInfo }, worksheetId);
            selection.getSummaryMap;
            updateDocuments(worksheetId, summary);
          } else {
            const newIndex = (codingTabIndex.getState().index + 1) % worksheets.length;
            setWorksheetIndex(newIndex);
            codingTabIndex.setState({ index: newIndex });
            const worksheetId = worksheets[newIndex].context.worksheet.worksheetId;
            updateDocuments(worksheetId, summary);
          }
        }
      } else {
        updateDocuments(summary.worksheetId, summary);
      }
    }
  }
  const apis = getApis();
  const apisById = (id: string) => {
    const a = getApiById(id);
    apis.length = 0;
    apis.push(...getApis());
    return a;
  };

  function closeTransitionDialogs() {
    apis.forEach((a) => {
      a.transition.setState({ openTransitionDialogue: false, transitionDialogueComplete: null });
    });
  }

  function selectedApis(id?: string) {
    id = id || data.getState().selectedId || selectedId;
    if (id) {
      return apisById(id);
    }
    let tabApi = getWorksheet();
    const apiId = tabApi.context.worksheet.worksheetId;
    return apisById(apiId);
  }

  // TODO: check with Nicki to see whether copying to all worksheets is desirable
  // apis.forEach((v) => v.icdCpt.addCpt({ code: a.cptCode }, true, false, a));
  // apis.forEach((v) => v.icdCpt.addIcd(a));

  function visitOtherTabs(id: string, fn: (a: WorksheetApis) => void) {
    if (worksheets.length > 0) {
      const other = worksheets.filter((a) => a.context.worksheet.worksheetId !== id);
      other.forEach((a) => {
        fn(a);
      });
    }
  }
  let doSort = true;

  function rerenderTuples() {
    data.setState({ tuples: getCurrentWorksheetData(selectedApis()) });
    doSort = true;
  }

  const addCpt = (a: Procedure, id: string) => {
    doSort = false;
    const apis = selectedApis(id);

    worksheets.forEach((a) => a.icdCpt.setWorksheetReplacement(false));

    visitOtherTabs(apis.context.worksheet.worksheetId, (v) =>
      v.icdCpt.addCpt({ code: a.cptCode }, true, false, {
        description: a.description,
        globalDays: a.globalDays,
        modifiers: a.modifiers,
        modifier: modifierLinkString(a),
        provider: a.provider,
        secondaryProvider: a.secondaryProvider,
        tertiaryProvider: a.tertiaryProvider,
      })
    );

    worksheets.forEach((a) => a.icdCpt.setWorksheetReplacement(true));

    rerenderTuples();
  };

  function disableAlerts() {
    worksheets.forEach((a) => a.icdCpt.setWorksheetReplacement(false));
  }
  function enableAlerts() {
    setTimeout(() => {
      worksheets.forEach((a) => a.icdCpt.setWorksheetReplacement(true));
    }, 10);
  }

  const addIcd = (a: Diagnosis, id: string) => {
    doSort = false;
    const apis = selectedApis(id);
    visitOtherTabs(apis.context.worksheet.worksheetId, (v) => v.icdCpt.addIcd(a));
    rerenderTuples();
  };
  const allIcd = (a: Diagnosis[], id: string) => {
    a.forEach((v) => addIcd(v, id));
  };
  const allCpt = (a: Procedure[], id: string) => {
    a.forEach((v) => addCpt(v, id));
  };

  const copyWorksheet = (a: Worksheet, id: string) => {
    allIcd(a.diagnoses, id || selectedId);
    allCpt(a.procedures, id || selectedId);
  };

  return {
    data,
    apis,
    getWorksheet,
    setWorksheet,
    apisById,
    selectedApis,
    updateSummary,
    buildTuples,
    addCpt: (a: Procedure, id?: string) => {
      disableAlerts();
      addCpt(a, id);
      enableAlerts();
    },
    addIcd: (a: Diagnosis, id?: string) => {
      disableAlerts();
      addIcd(a, id);
      enableAlerts();
    },
    allIcd: (a: Diagnosis[], id?: string) => {
      disableAlerts();
      allIcd(a, id);
      enableAlerts();
    },
    allCpt: (a: Procedure[], id?: string) => {
      disableAlerts();
      allCpt(a, id);
      enableAlerts();
    },
    copyWorksheet: (a: Worksheet, id?: string) => {
      disableAlerts();
      copyWorksheet(a, id);
      enableAlerts();
    },
    quit,
    destroy,
    updateDocuments,
    closeTransitionDialogs,
  };
}
